import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import DeleteModal from './modals/DeleteModal';
import ManageImages from './modals/ManageImages';
import Spinner from './Spinner';
import { Button } from 'react-bootstrap';
import { FaEdit, FaTrash, FaCogs, FaEye, FaEyeSlash, FaChevronLeft, FaChevronRight, FaEraser, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';

import { useRef, useEffect } from 'react';
import '../styles/StyleTable.css';

const StyleTable = ({ 
  styles, 
  loading, 
  tableActions, 
  handleRowClick,
  BASE_URL,
  onAfterDelete,
  userRole
}) => {
  const [deleteModalId, setDeleteModalId] = useState(null);
  const [, setDeleteModalStyle] = useState(null);
  const [manageImagesStyle, setManageImagesStyle] = useState(null);

  // Track page index to preserve it across data refreshes
  const pageIndexRef = useRef(0);

  const columns = useMemo(
    () => [
      {
        header: "Images",
        accessorKey: "image",
        maxWidth: 100,
        cell: ({ getValue, row }) => (
          <div 
            className="table-images"
            onClick={(e) => {
              e.stopPropagation();
              setManageImagesStyle(row.original);
            }}
            style={{ cursor: 'pointer' }}
          >
            {getValue() ? (
              <img
                src={`${BASE_URL}/uploads/styles/${getValue().trim()}`}
                alt="Primary"
                className="table-thumbnail"
                title="Click to manage images"
              />
            ) : (
              <span className="text-muted">No image</span>
            )}
          </div>
        ),
      },
      {
        header: "Product number",
        accessorKey: "style_number",
        maxWidth: 80,
      },
      {
        header: "Name",
        accessorKey: "name",
        maxWidth: 150,
      },
      {
        header: "Description",
        accessorKey: "description",
        maxWidth: 200,
        cell: ({ getValue }) => (
          <div
            title={getValue()}
            style={{
              maxWidth: "200px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {getValue()}
          </div>
        ),
      },
      {
        header: "Main Cat.",
        accessorKey: "main_category_name",
        maxWidth: 100,
      },
      {
        header: "Sub Cat.",
        accessorKey: "subcategory_name",
        maxWidth: 100,
      },
      {
        header: "Status",
        accessorKey: "approved",
        maxWidth: 80,
        cell: ({ getValue }) => (
          <span
            className={`status-badge ${
              getValue() === "yes" ? "approved" : "pending"
            }`}
          >
            {getValue() === "yes" ? "Approved" : "Pending"}
          </span>
        ),
      },
      {
        header: "Publish",
        accessorKey: "is_view",
        maxWidth: 80,
        cell: ({ row }) => {
          const style = row.original;
          const isView = style.is_view === "yes";
          // Get error message from parent
          const errorMsg =
            typeof tableActions.getIsViewError === "function"
              ? tableActions.getIsViewError(style.style_id)
              : "";
          return (
            <div className="table-visibility">
              {isView ? (
                <FaEye
                  className="action-icon me-2 text-success"
                  title="Set Not Viewable"
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (typeof tableActions.handleToggleIsView === "function") {
                      await tableActions.handleToggleIsView(style, !isView);
                    }
                  }}
                  style={{ cursor: "pointer" }}
                  size={16}
                />
              ) : (
                <FaEyeSlash
                  className="action-icon me-2 text-muted"
                  title="Set Viewable"
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (typeof tableActions.handleToggleIsView === "function") {
                      await tableActions.handleToggleIsView(style, !isView);
                    }
                  }}
                  style={{ cursor: "pointer" }}
                  size={16}
                />
              )}
              {errorMsg && (
                <div style={{ color: "red", fontSize: 12, marginTop: 2 }}>
                  {errorMsg}
                </div>
              )}
            </div>
          );
        },
      },
      {
        header: "Marketplace Main Cat.",
        accessorKey: "marketplace_main_category_name",
        maxWidth: 120,
      },
      {
        header: "Marketplace Sub Cat.",
        accessorKey: "marketplace_subcategory_name",
        maxWidth: 120,
      },
      {
        header: (
          <div style={{ textAlign: "center" }}>
            Publish on <br /> Common Website
          </div>
        ),
        accessorKey: "publish_common",
        maxWidth: 80,
        cell: ({ row }) => {
          const style = row.original;
          const isPublishCommon = style.publish_common === "yes";
          // Get error message from parent
          const errorMsg =
            typeof tableActions.getPublishCommonError === "function"
              ? tableActions.getPublishCommonError(style.style_id)
              : "";
          return (
            <div className="table-visibility">
              {isPublishCommon ? (
                <FaEye
                  className="action-icon me-2 text-success"
                  title="Disable Common Website Publishing"
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (
                      typeof tableActions.handleTogglePublishCommon ===
                      "function"
                    ) {
                      await tableActions.handleTogglePublishCommon(
                        style,
                        !isPublishCommon
                      );
                    }
                  }}
                  style={{ cursor: "pointer" }}
                  size={16}
                />
              ) : (
                <FaEyeSlash
                  className="action-icon me-2 text-muted"
                  title="Enable Common Website Publishing"
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (
                      typeof tableActions.handleTogglePublishCommon ===
                      "function"
                    ) {
                      await tableActions.handleTogglePublishCommon(
                        style,
                        !isPublishCommon
                      );
                    }
                  }}
                  style={{ cursor: "pointer" }}
                  size={16}
                />
              )}
              {errorMsg && (
                <div style={{ color: "red", fontSize: 12, marginTop: 2 }}>
                  {errorMsg}
                </div>
              )}
            </div>
          );
        },
      },
      {
        header: "Actions",
        maxWidth: 200,
        cell: ({ row }) => {
          const style = row.original;
          const isApproved = style.approved === "yes";
          return (
            <div className="table-actions">
              <FaEdit
                className="action-icon me-2"
                onClick={(e) => {
                  e.stopPropagation();
                  tableActions.handleEditStyle(style);
                }}
                title="Edit Product"
                style={{
                  cursor: "pointer",
                  fontSize: "16px",
                  color: "#007bff",
                }}
              />
              <FaTrash
                className="action-icon me-2 text-danger"
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteModalId(style.style_id);
                }}
                title="Delete Product"
                style={{ cursor: "pointer" }}
                size={16}
              />
              <FaCogs
                className="action-icon me-2 text-secondary"
                onClick={(e) => {
                  e.stopPropagation();
                  tableActions.handleManageAttributes(style);
                }}
                title="Manage Attributes"
                style={{ cursor: "pointer" }}
                size={16}
              />
              {userRole === "Company_Admin" &&
                (isApproved ? (
                  <FaTimesCircle
                    className="action-icon me-2 text-danger"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (
                        typeof tableActions.handleToggleApproval === "function"
                      ) {
                        tableActions.handleToggleApproval(style, false);
                      }
                    }}
                    title="Unapprove Product"
                    style={{ cursor: "pointer" }}
                    size={16}
                  />
                ) : (
                  <FaCheckCircle
                    className="action-icon me-2 text-success"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (
                        typeof tableActions.handleToggleApproval === "function"
                      ) {
                        tableActions.handleToggleApproval(style, true);
                      }
                    }}
                    title="Approve Product"
                    style={{ cursor: "pointer" }}
                    size={16}
                  />
                ))}
            </div>
          );
        },
      },
    ],
    [tableActions, BASE_URL, userRole]
  );


  const table = useReactTable({
    data: styles,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: { pageSize: 10, pageIndex: pageIndexRef.current }
    }
  });

  // Clear all column filters and global filter
  const clearFilters = () => {
    // Prefer built-in reset if available
    if (typeof table.resetColumnFilters === 'function') {
      table.resetColumnFilters();
    } else {
      table.getAllColumns().forEach(col => {
        if (typeof col.setFilterValue === 'function') {
          col.setFilterValue(undefined);
        } else if (typeof col.setFilter === 'function') {
          col.setFilter(undefined);
        }
      });
    }

    if (typeof table.setGlobalFilter === 'function') {
      table.setGlobalFilter(undefined);
    }
  };

  // When styles data changes, restore previous page index if possible
  useEffect(() => {
    // Save current page index before data changes
    const currentPage = table.getState().pagination.pageIndex;
    pageIndexRef.current = currentPage;
    // After data changes, restore page index if possible
    if (table.getPageCount() > pageIndexRef.current) {
      table.setPageIndex(pageIndexRef.current);
    } else {
      table.setPageIndex(0);
      pageIndexRef.current = 0;
    }
    // eslint-disable-next-line
  }, [styles]);

  const handleDeleteSuccess = (deletedId) => {
    setDeleteModalId(null);
    setDeleteModalStyle(null);
    if (typeof onAfterDelete === 'function') {
      onAfterDelete(deletedId);
    }
  };

  if (loading) {
    return <Spinner text="Loading styles..." />;
  }

  if (styles.length === 0) {
    return null;
  }

  return (
    <>
     <table className="styles-table">
        <thead>
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <th
                  key={header.id}
                  onClick={header.column.getToggleSortingHandler()}
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                  <span className="sort-indicator">
                    {header.column.getIsSorted()
                      ? header.column.getIsSorted() === "desc"
                        ? " 🔽"
                        : " 🔼"
                      : ""}
                  </span>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {/* Column filter row */}
          <tr className="filter-row">
            {table.getHeaderGroups()[0].headers.map(header => {
              const accessor = header.column.columnDef.accessorKey;
              const headerLabel = String(header.column.columnDef.header);
              return (
                <td key={`filter-${header.id}`}>
                  {/* Skip rendering a filter input for the Images column and Actions column */}
                  {!header.isPlaceholder && accessor !== 'image' && headerLabel !== 'Actions' ? (
                    // Render dropdown for Status (approved), Publish (is_view), and Publish on Common Website (publish_common)
                    (accessor === 'approved' || accessor === 'is_view' || accessor === 'publish_common') ? (
                      <select
                        className="column-filter-input"
                        value={typeof header.column.getFilterValue === 'function' ? header.column.getFilterValue() ?? '' : ''}
                        onChange={e => {
                          e.stopPropagation();
                          const v = e.target.value || undefined;
                          if (typeof header.column.setFilterValue === 'function') {
                            header.column.setFilterValue(v);
                          } else if (typeof header.column.setFilter === 'function') {
                            header.column.setFilter(v);
                          }
                        }}
                        onClick={e => e.stopPropagation()}
                      >
                        <option value="">All</option>
                        {accessor === 'approved' ? (
                          <>
                            <option value="yes">Approved</option>
                            <option value="no">Pending</option>
                          </>
                        ) : accessor === 'is_view' ? (
                          <>
                            <option value="yes">Viewable</option>
                            <option value="no">Not viewable</option>
                          </>
                        ) : (
                          <>
                            <option value="yes">Enabled</option>
                            <option value="no">Disabled</option>
                          </>
                        )}
                      </select>
                    ) : (
                      <input
                        type="text"
                        className="column-filter-input"
                        placeholder={`Search ${String(header.column.columnDef.header)}`}
                        value={typeof header.column.getFilterValue === 'function' ? header.column.getFilterValue() ?? '' : ''}
                        onChange={e => {
                          e.stopPropagation();
                          if (typeof header.column.setFilterValue === 'function') {
                            header.column.setFilterValue(e.target.value || undefined);
                          } else if (typeof header.column.setFilter === 'function') {
                            // fallback if older API exists
                            header.column.setFilter(e.target.value || undefined);
                          }
                        }}
                        onClick={e => e.stopPropagation()}
                      />
                    )
                  ) : null}
                  {/* If this is the Actions column, render a Clear filters button in the first cell */}
                  {headerLabel === 'Actions' ? (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Button
                        variant="outline"
                        className="pf-clear-filters-btn"
                        onClick={(e) => { e.stopPropagation(); clearFilters(); }}
                        title="Clear filters"
                      >
                        <FaEraser />
                      </Button>
                    </div>
                  ) : null}
                </td>
              );
            })}
          </tr>
          {table.getRowModel().rows.map(row => (
            <tr
              key={row.id}
              onClick={() => handleRowClick(row.original)}
              className="clickable-row"
            >
              {row.getVisibleCells().map(cell => (
                <td key={cell.id}>
                  {flexRender(
                    cell.column.columnDef.cell,
                    cell.getContext()
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      <div className="pagination">
        <button
          onClick={() => table.previousPage()}
          disabled={!table.getCanPreviousPage()}
          className="btn btn-outline-secondary btn-sm me-2"
        >
          <FaChevronLeft className="me-1" /> Previous
        </button>
        <span className="pagination-info">
          Page{' '}
          <strong>
            {table.getState().pagination.pageIndex + 1} of{' '}
            {table.getPageCount()}
          </strong>
        </span>
        <button
          onClick={() => table.nextPage()}
          disabled={!table.getCanNextPage()}
          className="btn btn-outline-secondary btn-sm ms-2"
        >
          Next <FaChevronRight className="ms-1" />
        </button>
      </div>
      <DeleteModal
        id={deleteModalId}
        show={!!deleteModalId}
        onHide={() => setDeleteModalId(null)}
        deleteUrl={id => id ? `/api/admin/styles/delete-styles/${id}` : ''}
        entityLabel="style"
        onDeleteSuccess={handleDeleteSuccess}
      />
      <ManageImages
        show={!!manageImagesStyle}
        onHide={() => setManageImagesStyle(null)}
        styleId={manageImagesStyle?.style_id}
        currentImages={manageImagesStyle?.image || ''}
        BASE_URL={BASE_URL}
        onSaveImages={(newImages) => {
          // Refresh the parent component to get updated data
          if (typeof onAfterDelete === 'function') {
            onAfterDelete('refresh');
          }
          setManageImagesStyle(null);
        }}
      />
    </>
  );
};

export default StyleTable;
